import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { interval, take } from 'rxjs';
import { StateService } from '../../../shared/services/state.service';
import { WHATS_NEW_RULES, WhatsNewItem } from '../whats-new.config';
import { supportWhatsAppUrl } from '../whats-new.filter';
import { createStoryVideo, releaseStoryVideo } from '../whats-new-video';

// Id do título: quem abre a modal passa em `ariaLabelledBy` do NgbModal. Só existe um título (o da
// novidade atual), então o rótulo da modal acompanha a troca.
export const WHATS_NEW_TITLE_ID = 'whats-new-title';

/** Segurar o dedo/mouse no vídeo por esse tempo, sem arrastar, pausa. */
const HOLD_MS = 250;
/** Arrasto que troca de novidade (ou gesto rápido em qualquer distância). */
const SWIPE_PX = 60;
/** Abaixo disso o movimento ainda é toque, não arrasto. */
const DRAG_START_PX = 8;
/** Velocidade (px/ms) que conta como gesto rápido. */
const FAST_PX_PER_MS = 0.5;
/** Toque nesta fração da esquerda volta; no resto avança. */
const TAP_BACK_RATIO = 0.3;
/** Arrasto além da primeira/última novidade anda só 1/3 do dedo. */
const EDGE_RESISTANCE = 1 / 3;
/** Vídeo que não fica pronto (iOS não pré-carrega sem gesto) começa mesmo assim depois disso. */
const READY_FALLBACK_MS = 2000;
/** A legenda some, troca o texto e volta. */
const CAPTION_FADE_MS = 150;
/** Desktop: largura máxima do cartão e mínimo pra janela muito baixa. */
const CARD_MAX_W = 430;
const CARD_MIN_W = 240;
/** Vídeo 4:5: largura = altura × 0,8. */
const MEDIA_RATIO = 4 / 5;

interface Gesture {
  x: number;
  t: number;
  dx: number;
  drag: boolean;
  hold?: ReturnType<typeof setTimeout>;
}

// Modal das novidades em stories (estilo Instagram). Recebe todas as pendentes e mostra uma por vez:
// uma barra por novidade no topo enche com o vídeo, que começa `startDelay` depois de aparecer e para
// em `sceneEnd` (depois disso o vídeo escurece pro loop); então passa sozinho pra próxima em
// `advanceAfter`. Troca por arrasto, toque (30 % da esquerda volta), setas no desktop e teclado.
// A trava de `lockSeconds` é uma só pra modal toda: o X e o "Fechar" só liberam depois dela. O botão
// da novidade fecha na hora, mesmo na trava. Quem abre (WhatsNewService) passa `onShow`, chamado
// uma vez por novidade quando ela aparece, e espera o `result`: 'action' | 'close'.
@Component({
  selector: 'app-whats-new-modal',
  standalone: true,
  templateUrl: './whats-new-modal.component.html',
  styleUrl: './whats-new-modal.component.scss',
})
export class WhatsNewModalComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input({ required: true }) items!: WhatsNewItem[];
  /** Chamado uma vez por novidade, quando ela aparece (a primeira ao abrir). */
  @Input() onShow: (item: WhatsNewItem) => void = () => undefined;
  /** Vídeos que o WhatsNewService já vinha baixando, por id. A modal adota (e libera ao fechar). */
  @Input() preloaded: ReadonlyMap<string, HTMLVideoElement> = new Map();

  private readonly modal = inject(NgbActiveModal);
  private readonly router = inject(Router);
  private readonly state = inject(StateService);
  private readonly zone = inject(NgZone);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  private readonly mediaEl = viewChild.required<ElementRef<HTMLElement>>('media');
  // Opcional: a primeira novidade é ativada no ngOnInit, antes da view existir.
  private readonly trackEl = viewChild<ElementRef<HTMLElement>>('track');
  private readonly bodyEl = viewChild.required<ElementRef<HTMLElement>>('body');
  private readonly slideEls = viewChildren<ElementRef<HTMLElement>>('slide');
  /** Um <video> por novidade (vazio com movimento reduzido), adotado ou criado no ngAfterViewInit. */
  private videos: HTMLVideoElement[] = [];
  private readonly fillEls = viewChildren<ElementRef<HTMLElement>>('fill');

  readonly lockSeconds = WHATS_NEW_RULES.lockSeconds;
  readonly titleId = WHATS_NEW_TITLE_ID;
  /** Segundos que faltam pra destravar (de `lockSeconds` a 0). */
  readonly remaining = signal<number>(this.lockSeconds);
  readonly locked = computed(() => this.remaining() > 0);
  /** O som começa desligado (autoplay só roda mudo) e vale pra modal toda. */
  readonly muted = signal(true);
  /** Movimento reduzido: pôster parado no lugar do vídeo, sem autoplay nem avanço. */
  readonly reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /** Novidade atual (vídeo e barras). */
  readonly index = signal(0);
  /** Novidade da legenda: troca no meio do fade. */
  readonly captionIndex = signal(0);
  readonly captionFading = signal(false);
  /** Dedo/mouse segurando o vídeo (pausado). */
  readonly held = signal(false);

  /** Cena terminou (vídeo parado no fim, barra cheia). */
  private done: boolean[] = [];
  private readonly shown = new Set<string>();
  private gesture: Gesture | null = null;
  private starting = false;
  /** Abertura da modal: a primeira novidade espera o fim da entrada do cartão. */
  private openedAt = 0;
  /** Cada ativação ganha um número; espera de ativação anterior não inicia o vídeo errado. */
  private waitToken = 0;
  private entranceTimer?: ReturnType<typeof setTimeout>;
  private readyFallback?: ReturnType<typeof setTimeout>;
  private readyCleanup?: () => void;
  private startTimer?: ReturnType<typeof setTimeout>;
  private advanceTimer?: ReturnType<typeof setTimeout>;
  private captionTimer?: ReturnType<typeof setTimeout>;
  private raf = 0;
  private fitRaf = 0;
  private resizeObserver?: ResizeObserver;
  private readonly cleanup: (() => void)[] = [];

  constructor() {
    // Termina sozinho em `lockSeconds` ticks; o takeUntilDestroyed cobre fechar a modal antes disso.
    interval(1000)
      .pipe(take(this.lockSeconds), takeUntilDestroyed(inject(DestroyRef)))
      .subscribe((tick) => this.remaining.set(this.lockSeconds - (tick + 1)));
  }

  get last(): number {
    return this.items.length - 1;
  }

  get caption(): WhatsNewItem {
    return this.items[this.captionIndex()];
  }

  get primaryLabel(): string | null {
    return this.labelOf(this.caption);
  }

  labelOf(item: WhatsNewItem): string | null {
    const action = item.action;
    switch (action.type) {
      case 'open':
        return action.label;
      case 'activate':
        return 'Ativar agora';
      case 'support':
        return 'Pedir ativação';
      default:
        return null;
    }
  }

  ngOnInit(): void {
    this.done = this.items.map(() => false);
    this.openedAt = Date.now();
    this.activate(0, true);
  }

  ngAfterViewInit(): void {
    this.adoptVideos();
    this.paintBars();
    this.zone.runOutsideAngular(() => {
      const media = this.mediaEl().nativeElement;
      const on = <K extends keyof HTMLElementEventMap>(
        target: HTMLElement | Window,
        type: K,
        fn: (e: HTMLElementEventMap[K]) => void,
      ): void => {
        target.addEventListener(type, fn as EventListener);
        this.cleanup.push(() => target.removeEventListener(type, fn as EventListener));
      };
      on(media, 'pointerdown', (e) => this.pointerDown(e));
      on(media, 'pointermove', (e) => this.pointerMove(e));
      on(media, 'pointerup', (e) => this.pointerUp(e));
      on(media, 'pointercancel', () => this.pointerCancel());

      // Desktop: o cartão segue a altura da janela (vídeo menor, nunca cortado nem com rolagem).
      const win = this.host.nativeElement.ownerDocument.defaultView;
      if (win) on(win, 'resize', () => this.scheduleFit());
      if (typeof ResizeObserver !== 'undefined') {
        // No quadro seguinte: mudar a largura dentro do callback muda a altura da legenda observada
        // e o navegador acusa "ResizeObserver loop".
        this.resizeObserver = new ResizeObserver(() => this.scheduleFit());
        this.resizeObserver.observe(this.bodyEl().nativeElement);
      }
      this.fit();

      if (!this.reducedMotion) this.raf = requestAnimationFrame(this.frame);
    });
  }

  ngOnDestroy(): void {
    cancelAnimationFrame(this.raf);
    cancelAnimationFrame(this.fitRaf);
    this.cancelStart();
    clearTimeout(this.advanceTimer);
    clearTimeout(this.captionTimer);
    if (this.gesture) clearTimeout(this.gesture.hold);
    this.resizeObserver?.disconnect();
    this.cleanup.forEach((fn) => fn());
    // Para o download do preload dos vídeos que ainda estavam carregando.
    this.videos.forEach((v) => releaseStoryVideo(v));
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      this.close();
      return;
    }
    const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    const target = this.index() + step;
    // Além da ponta não faz nada (não reinicia o vídeo que está tocando).
    if (step && target >= 0 && target <= this.last) this.go(target);
  }

  // O Chrome pausa vídeo mudo em aba escondida; ao voltar, a novidade continua de onde parou.
  @HostListener('document:visibilitychange')
  onVisibilityChange(): void {
    if (document.visibilityState !== 'visible' || this.reducedMotion || this.starting) return;
    const i = this.index();
    const video = this.video(i);
    if (video && !this.done[i] && !this.held() && video.paused) this.play();
  }

  /** Vai pra novidade `i` (limitada às pontas). */
  go(i: number): void {
    this.activate(i);
  }

  toggleSound(): void {
    this.muted.update((m) => !m);
    this.applyMuted();
  }

  /** Fecha sem ação. Ignorado durante a trava (vale pro Esc também). */
  close(): void {
    if (this.locked()) return;
    this.modal.close('close');
  }

  runAction(): void {
    const action = this.caption.action;
    switch (action.type) {
      case 'open':
      case 'activate':
        void this.router.navigateByUrl(action.route);
        break;
      case 'support': {
        const tenant = this.state.tenant$.value;
        const company = tenant ? (tenant.fantasyName ?? tenant.name) : null;
        window.open(supportWhatsAppUrl(action.subject, company), '_blank', 'noopener');
        break;
      }
      default:
        return;
    }
    this.modal.close('action');
  }

  // --- novidade atual ---

  private activate(target: number, first = false): void {
    const i = Math.max(0, Math.min(this.last, target));
    this.cancelStart();
    clearTimeout(this.advanceTimer);
    this.held.set(false);
    // Dedo que estava no vídeo da novidade anterior (ex.: avanço automático) não vira toque nem
    // segurar nesta.
    this.cancelGesture();

    const changed = i !== this.index();
    this.videos.forEach((v, j) => {
      v.pause();
      if (j !== i) v.currentTime = 0;
    });
    this.index.set(i);
    // Mesma novidade já terminada (toque à esquerda na primeira) fica parada no fim.
    if (first || changed || !this.done[i]) {
      this.done[i] = false;
      const video = this.video(i);
      if (video) video.currentTime = 0;
    }

    this.setTrack(0);
    this.showCaption(i, first || i === this.captionIndex());
    this.paintBars();
    this.notifyShown(i);

    if (this.done[i]) {
      // Continua contando pra próxima (o toque cancelou o avanço agendado).
      this.starting = false;
      this.scheduleAdvance(i);
    } else if (!this.reducedMotion) {
      this.scheduleStart();
    } else {
      this.starting = false;
    }
  }

  // Pequena pausa antes do movimento (o olho acha a novidade antes de o vídeo andar), contada do
  // vídeo pronto e, na abertura, do fim da entrada do cartão: carregamento e pausa não se somam.
  // Pronto = readyState >= 2 ou `canplay` (não `loadeddata`: depois de voltar pro 0 o vídeo
  // recarrega e o loadeddata não dispara de novo).
  private scheduleStart(): void {
    const token = ++this.waitToken;
    this.starting = true;
    const delay = (): void => {
      if (token !== this.waitToken) return;
      this.clearReadyWait();
      this.startTimer = setTimeout(() => {
        if (token !== this.waitToken) return;
        this.starting = false;
        this.play();
      }, WHATS_NEW_RULES.startDelay * 1000);
    };
    const whenReady = (): void => {
      if (token !== this.waitToken) return;
      const video = this.video(this.index());
      if (!video || video.readyState >= 2) {
        delay();
        return;
      }
      const onReady = (): void => delay();
      video.addEventListener('canplay', onReady, { once: true });
      this.readyCleanup = () => video.removeEventListener('canplay', onReady);
      this.readyFallback = setTimeout(delay, READY_FALLBACK_MS);
    };
    const entranceLeft = this.openedAt + WHATS_NEW_RULES.entrance * 1000 - Date.now();
    if (entranceLeft > 0) this.entranceTimer = setTimeout(whenReady, entranceLeft);
    else whenReady();
  }

  private clearReadyWait(): void {
    clearTimeout(this.readyFallback);
    this.readyCleanup?.();
    this.readyCleanup = undefined;
  }

  /** Cancela a espera pra começar (entrada, vídeo pronto, pausa) da novidade anterior. */
  private cancelStart(): void {
    this.waitToken++;
    clearTimeout(this.entranceTimer);
    clearTimeout(this.startTimer);
    this.clearReadyWait();
  }

  // Vídeo pré-carregado entra no slide; o que faltou (ex.: segunda novidade sem preload) é criado
  // aqui. Os estilos vão inline: o elemento não leva o atributo do encapsulamento do componente.
  private adoptVideos(): void {
    if (this.reducedMotion) return;
    const slides = this.slideEls();
    this.videos = this.items.map((item, i) => {
      const video = this.preloaded.get(item.id) ?? createStoryVideo(item.videoUrl);
      video.muted = this.muted();
      Object.assign(video.style, {
        display: 'block',
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        pointerEvents: 'none',
      });
      slides[i]?.nativeElement.appendChild(video);
      return video;
    });
  }

  private applyMuted(): void {
    const muted = this.muted();
    this.videos.forEach((v) => (v.muted = muted));
  }

  private notifyShown(i: number): void {
    const item = this.items[i];
    if (this.shown.has(item.id)) return;
    this.shown.add(item.id);
    this.onShow(item);
  }

  private showCaption(i: number, immediate: boolean): void {
    clearTimeout(this.captionTimer);
    if (immediate || this.reducedMotion) {
      this.captionIndex.set(i);
      this.captionFading.set(false);
      return;
    }
    this.captionFading.set(true);
    this.captionTimer = setTimeout(() => {
      this.captionIndex.set(i);
      this.captionFading.set(false);
    }, CAPTION_FADE_MS);
  }

  private video(i: number): HTMLVideoElement | undefined {
    return this.videos[i];
  }

  private play(): void {
    const i = this.index();
    const video = this.video(i);
    if (!video || this.done[i] || this.held()) return;
    video.muted = this.muted();
    video.play().catch((err: unknown) => {
      // O WebKit (iOS) recusa play() com som fora de um gesto (NotAllowedError), como no avanço
      // automático: volta pro mudo e tenta de novo, em vez de a novidade ficar parada no primeiro
      // quadro. Outras recusas (AbortError: a troca de novidade pausou o play pendente) e vídeo que
      // já saiu de cena não mexem na escolha de som.
      const notAllowed = (err as { name?: string } | null)?.name === 'NotAllowedError';
      if (!notAllowed || video.muted || video !== this.video(this.index())) return;
      this.muted.set(true);
      this.applyMuted();
      video.play().catch((): void => undefined);
    });
  }

  /** Fim da cena: para no último quadro da solução e agenda a próxima. */
  private finishScene(i: number): void {
    this.video(i)?.pause();
    this.done[i] = true;
    this.paintBars();
    this.scheduleAdvance(i);
  }

  private scheduleAdvance(i: number): void {
    clearTimeout(this.advanceTimer);
    if (this.reducedMotion || i >= this.last) return;
    this.advanceTimer = setTimeout(() => this.activate(i + 1), WHATS_NEW_RULES.advanceAfter * 1000);
  }

  // Roda fora do Angular: escreve as barras direto no DOM a cada quadro e só entra na zona pra
  // mudar estado (fim da cena).
  private readonly frame = (): void => {
    this.paintBars();
    const i = this.index();
    const video = this.video(i);
    if (video && !this.done[i] && video.currentTime >= this.sceneEnd(video)) {
      this.zone.run(() => this.finishScene(i));
    }
    this.raf = requestAnimationFrame(this.frame);
  };

  /** Fim da cena; um vídeo mais curto que `sceneEnd` acaba no fim dele (sem travar). */
  private sceneEnd(video: HTMLVideoElement): number {
    return Math.min(WHATS_NEW_RULES.sceneEnd, video.duration || Infinity);
  }

  /** Anteriores cheias, seguintes vazias, a atual acompanha o vídeo até o fim da cena. */
  private paintBars(): void {
    const i = this.index();
    this.fillEls().forEach(({ nativeElement: fill }, j) => {
      let p: number;
      if (j !== i) p = j < i ? 1 : 0;
      else if (this.reducedMotion || this.done[j]) p = 1;
      else {
        const video = this.video(j);
        p = video ? Math.min(1, video.currentTime / this.sceneEnd(video)) : 0;
      }
      fill.style.transform = `scaleX(${p})`;
    });
  }

  // --- gestos no vídeo ---

  private setTrack(dx: number): void {
    const track = this.trackEl()?.nativeElement;
    if (!track) return;
    const base = -this.index() * 100;
    track.style.transform = dx ? `translateX(calc(${base}% + ${dx}px))` : `translateX(${base}%)`;
  }

  private pointerDown(e: PointerEvent): void {
    if (e.button > 0) return;
    this.gesture = {
      x: e.clientX,
      t: Date.now(),
      dx: 0,
      drag: false,
      // Sem vídeo andando (movimento reduzido) não há o que pausar.
      hold: this.reducedMotion ? undefined : setTimeout(() => this.zone.run(() => this.hold()), HOLD_MS),
    };
    try {
      this.mediaEl().nativeElement.setPointerCapture(e.pointerId);
    } catch {
      // Evento sem ponteiro ativo (ex.: sintético): segue sem captura.
    }
  }

  private pointerMove(e: PointerEvent): void {
    const g = this.gesture;
    if (!g) return;
    g.dx = e.clientX - g.x;
    if (!g.drag && Math.abs(g.dx) > DRAG_START_PX) {
      g.drag = true;
      clearTimeout(g.hold);
      this.trackEl()?.nativeElement.classList.add('is-dragging');
    }
    if (g.drag) {
      const i = this.index();
      const edge = (i === 0 && g.dx > 0) || (i === this.last && g.dx < 0);
      this.setTrack(edge ? g.dx * EDGE_RESISTANCE : g.dx);
    }
  }

  private pointerUp(e: PointerEvent): void {
    const g = this.gesture;
    if (!g) return;
    this.gesture = null;
    clearTimeout(g.hold);
    this.trackEl()?.nativeElement.classList.remove('is-dragging');

    this.zone.run(() => {
      const i = this.index();
      if (g.drag) {
        const fast = Math.abs(g.dx) / Math.max(1, Date.now() - g.t) > FAST_PX_PER_MS;
        if ((g.dx < -SWIPE_PX || (fast && g.dx < 0)) && i < this.last) this.activate(i + 1);
        else if ((g.dx > SWIPE_PX || (fast && g.dx > 0)) && i > 0) this.activate(i - 1);
        else {
          this.setTrack(0);
          if (this.held()) this.release();
        }
      } else if (this.held()) {
        this.release();
      } else {
        const r = this.mediaEl().nativeElement.getBoundingClientRect();
        if (e.clientX - r.left < r.width * TAP_BACK_RATIO) this.activate(i - 1);
        else if (i < this.last) this.activate(i + 1);
      }
    });
  }

  private cancelGesture(): void {
    if (!this.gesture) return;
    clearTimeout(this.gesture.hold);
    this.gesture = null;
    this.trackEl()?.nativeElement.classList.remove('is-dragging');
  }

  private pointerCancel(): void {
    const g = this.gesture;
    if (!g) return;
    this.gesture = null;
    clearTimeout(g.hold);
    this.trackEl()?.nativeElement.classList.remove('is-dragging');
    this.setTrack(0);
    if (this.held()) this.zone.run(() => this.release());
  }

  private hold(): void {
    this.held.set(true);
    this.video(this.index())?.pause();
    clearTimeout(this.advanceTimer);
  }

  /** Soltou: retoma a cena; se ela já tinha acabado, volta a contar pra próxima. */
  private release(): void {
    this.held.set(false);
    const i = this.index();
    if (this.done[i]) this.scheduleAdvance(i);
    else if (!this.starting) this.play();
  }

  // --- tamanho no desktop ---

  private scheduleFit(): void {
    cancelAnimationFrame(this.fitRaf);
    this.fitRaf = requestAnimationFrame(() => this.fit());
  }

  // largura = min(430, (altura da janela − margens do diálogo − legenda) × 0,8). Só vale >= sm (o
  // scss usa a variável só no desktop); no celular o diálogo é tela cheia.
  // A legenda cresce quando o cartão estreita, então a conta parte sempre de 430px e desce até
  // estabilizar: partir da largura atual pode prender o cartão estreito demais (estreito → legenda
  // alta → mais estreito).
  private fit(): void {
    const host = this.host.nativeElement;
    const doc = host.ownerDocument;
    const win = doc.defaultView;
    if (!win) return;
    const rem = parseFloat(win.getComputedStyle(doc.documentElement).fontSize) || 16;
    // 2 × --bs-modal-margin (1.75rem), igual ao max-height do .modal-content em styles.scss.
    const avail = doc.documentElement.clientHeight - 3.5 * rem;
    const body = this.bodyEl().nativeElement;
    // Piso: a linha de botões mais larga (medidores) cabe sem quebrar. Se isso passar da conta da
    // altura, o vídeo encolhe um pouco (inteiro, com faixas) em vez de o botão quebrar.
    const style = win.getComputedStyle(body);
    const pad = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
    const rows = Array.from(body.querySelectorAll<HTMLElement>('.whats-new__acts--sizer'));
    // Largura fracionária (offsetWidth arredonda pra baixo e o botão de verdade quebraria) + 1px de folga.
    const buttons = Math.ceil(Math.max(0, ...rows.map((r) => r.getBoundingClientRect().width)) + pad) + 1;
    const min = Math.min(CARD_MAX_W, Math.max(CARD_MIN_W, buttons));
    let width = CARD_MAX_W;
    for (let k = 0; k < 5; k++) {
      host.style.setProperty('--whats-new-w', `${width}px`);
      const next = Math.max(min, Math.min(CARD_MAX_W, Math.floor((avail - body.offsetHeight) * MEDIA_RATIO)));
      if (next >= width) break;
      width = next;
    }
    host.style.setProperty('--whats-new-w', `${width}px`);
  }
}
